import _get from 'lodash/get';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import _cloneDeep from 'lodash/cloneDeep';
import { ruid, post } from '@/lib/utils';
import { constants } from '@/lib/constants';
import {
  setFilterRowProps,
  constructSavedFilterObject,
  constructUQLString,
  mapperRelationships,
  formatFilterUiRows,
  validateQuery,
  setFields,
} from '@/lib/advancedSearchUtils';

const localState = {
  // camelCase
  uqlItems: {},
  recentQueries: {},
};

const localGetters = {
  // underscore_lowercase
  uqlItems: state => queryKey => state.uqlItems[queryKey],
  uqlItem: state => (key, queryKey) => {
    const items = _get(state, `uqlItems[${queryKey}].items`, []);
    return items.find(item => item._key === key);
  },
  savedFilters: state => queryKey => {
    const item = _get(state, `uqlItems.${queryKey}`);
    if (!item) return [];
    const filters = item.savedFilters;
    return filters || [];
  },
  recentQueries: state => queryKey => state.recentQueries[queryKey],
  associations: (state, getters, rootState, rootGetters) => (className, additionalFields) => {
    return mapperRelationships(className, rootGetters, additionalFields);
  },
  db_properties: (state, getters, rootState, rootGetters) => className => rootGetters['meta/db_properties'](className, {}, { advSearch: true }, ['db_schema', 'pseudo_column', 'mapper_property']),
  rows_contain_data: state => queryKey => {
    const items = _get(state, `uqlItems.${queryKey}.items`, []);
    const isDataGenerator = _get(state, `uqlItems.${queryKey}.dataGenerator`);
    if (isDataGenerator) return !!isDataGenerator;
    if (!items.length) return false;
    for (const { _uiRows } of items) {
      return _uiRows.some(row => {
        const { operator, userdate, usertext, field } = row;
        return operator || userdate || usertext || field;
      });
    }
  },
};

const localActions = {
  // camelCase
  addUqlItem({ commit }, payload) {
    commit('add_uql_items', payload);
  },
  setUqlItems({ commit }, payload) {
    commit('set_uql_items', payload);
  },
  deleteUqlItem({ commit }, payload) {
    commit('delete_uql_item', payload);
  },
  clearUqlItems({ commit }, queryKey) {
    commit('clear_uql_items', queryKey);
  },
  clearByQueryKey({ commit }, queryKey) {
    commit('clear_by_query_key', queryKey);
  },
  updateFilterRow({ commit }, payload) {
    commit('update_filter_row', payload);
  },
  initializeAdvSearch({ commit }, payload) {
    commit('init_adv_search', payload);
  },
  /**
   * Creates array of objects that can be display as chips and
   * will allow for editability in AdvancedSearch component
   * @param {Object} context.rootGetters
   * @param {Object} context.commit
   * @param {Object} filter - SavedFilter object from server
   * @return {Object[]} uqlItems (clauses, conjunctions, parens)
   */
  deconstructSavedFilter({ rootGetters }, filter) {
    const formattedFilter = formatFilterUiRows(filter, rootGetters, filter.classname);
    return formattedFilter;
  },
  /**
   * Creates an array of filterRow objects that can be saved or used to build a UQL string
   * @param {Object} context.state - object containing state object of advancedSearch store
   * @return {Object[]} array of filter row objects
   */
  constructSavedFilter({ state }, { queryKey, filter = {}, filterId = '' }) {
    const levels = setFilterRowProps(state.uqlItems[queryKey].items);
    const filterRows = constructSavedFilterObject(levels, filter, filterId, { ...state });
    filterRows._classname = 'SavedFilter';
    return filterRows;
  },
  /**
   * Uses the filter constructed in constructSavedFilter() to create a UQL query string
   * @param {Function} context.commit
   * @param {Function} context.dispatch
   * @param {Boolean} [cacheSearchString = true] - Determines if search string should be cached
   * @param {String} queryKey - Key used to get ulqItems objects to be transformed into a UQL search string
   * @param {String} className - Class name used to cache search string under
   * @return {String} UQL search string
   */
  async createUql({ commit, state, dispatch }, { cacheSearchString = true, queryKey, className }) {
    const rows = state.uqlItems[queryKey].items;
    const setRows = setFields(rows);

    const isValid = validateQuery(setRows);
    if (!isValid) {
      dispatch('notify/push', {
        title: 'Error',
        text: 'Your search is not fully specified.',
      }, { root: true });

      return;
    }

    const q = constructUQLString(setRows);
    if (cacheSearchString) commit('cached/set_search_string', { className, searchString: q }, { root: true });
    return q;
  },
  /**
   * CreateSavedFilter persists filters to database
   * @param {Object} context.state
   * @param {Object} context.rootGetters - used to for auth store getters
   * @param {Function} context.dispatch
   * @param {Object} payload
   * @return {Object} response from server
   */
  async createSavedFilter({ rootGetters, dispatch }, payload) {
    const { saveAsNew, permission, isPublic, filterId, queryKey, className, originalFilter = {} } = payload;
    const url = 'savedfilter/save';
    const baseFilter = await dispatch('constructSavedFilter', { queryKey, filter: originalFilter, filterId });

    const filter = {
      ...(originalFilter && { ...originalFilter }),
      ...baseFilter,
      username: isPublic ? null : rootGetters['auth/username'],
      permissionname: permission || null,
      classname: className,
    };

    if (saveAsNew) {
      filter.filterid = filterId;
      for (let i = 0; i < filter.rows.length; i += 1) {
        filter.rows[i].filterid = filterId;
      }
    }

    const { results, errors } = await post(`/tg/${url}`, { saveasnew: saveAsNew, SavedFilter: filter });
    return { results, errors };
  },
  /**
   * Deletes filter by id from server
   * @param {Object} context - Vuex context - not used in function, but is required for actions
   * @param {string} filterId - id of filter to be deleted
   * @return {Object} respose object to be used in notify growl
   */
  async deleteSavedFilter(context, filterId) {
    const params = { filterid: filterId };
    const url = 'savedfilter/delete';
    const { results } = await post(`/tg/${url}`, params);
    return results;
  },
  /**
   * Fetches all persisted filters for current className
   * @param {Object} context.rootGetters
   * @param {string} className
   */
  async fetchSavedFilters({ commit, rootGetters }, { className, queryKey }) {
    const { EQUALS, DELIM, ISNULL } = constants;
    const userName = rootGetters['auth/username'];
    const q = `classname-${EQUALS}${className}${DELIM}or(username-${ISNULL}${DELIM}username-${EQUALS}${userName}${DELIM})`;
    const params = {
      q,
      orderby: 'filterid',
      for_client_write: 'Y',
      serialize: '_ui_label',
    };
    const { results } = await post('/tg/savedfilter/filter_lookup', params);
    commit('set_saved_filters', { savedFilters: results, queryKey });
    return results;
  },
  /**
   * Sets the last executed filter and total number of items in response
   * @param {Function} context.commit
   * @param {Function} context.state
   * @param {String} queryKey - Key used to match uqlItems with currently viewed search.
   */
  saveRecentQuery({ commit, state }, queryKey) {
    let currentUql = state.uqlItems[queryKey].items;
    currentUql = _cloneDeep(currentUql);
    commit('set_recent_query', { currentUql });
  },
  addUIRow({ commit }, payload) {
    commit('add_ui_row', payload);
  },
  removeUiRowByIndex({ commit }, payload) {
    commit('remove_ui_row_by_index', payload);
  },
  clearLocalState({ commit }) {
    commit('clear_local_state');
  },
  displayAdvSearchMsg({ dispatch }) {
    dispatch('notify/push', {
      title: 'Filter Criteria',
      text: 'Note that you have existing filter criteria which will be combined with your advanced search.',
      type: 'info',
    }, { root: true });
  },
};

const localMutations = {
  // underscore_case
  /**
   * Adds uql item from to state.uqlItems by className
   * if item does not have a _key property, _key is added
   * @param {Object} state
   * @param {Object} item - uql item object created in AdvacnedSearch
   *                        contains userInput, object type, and filter row
   */
  add_uql_items(state, payload) {
    const { item, queryKey, clearFilterId } = payload;
    if (!item._id) item._id = ruid();
    if (clearFilterId) state.uqlItems[queryKey].filterId = '';
    state.uqlItems[queryKey].items.push(item);
  },
  /**
   * Addes ids to all the items of array and
   * sets array to state.uqlItems by className
   * if item does not have a _key property, _key is added
   * @param {Object} state
   * @param {Object[]} items Array of userInput objects
   */
  set_uql_items(state, payload) {
    const items = payload.formattedFilter || [];
    for (let i = 0; i < items.length; i += 1) {
      if (!items[i]._id) items[i]._id = ruid();
    }
    const filterData = {
      items,
      filterId: payload.filterId,
    };

    filterData.dataGenerator = payload.dataGenerator || null;
    if (payload.args) filterData.args = payload.args;
    const uqlItem = state.uqlItems[payload.queryKey];
    state.uqlItems[payload.queryKey] = { ...uqlItem, ...filterData };
  },
  delete_uql_item(state, { key, queryKey }) {
    const classUqltems = state.uqlItems[queryKey].items;
    classUqltems.splice(classUqltems.findIndex(item => item._id === key), 1);
  },
  /**
   * Clears out uql for current className
   * @param {Object} state
   */
  clear_uql_items(state, queryKey) {
    const { savedFilters } = state.uqlItems[queryKey];
    state.uqlItems[queryKey] = { savedFilters, items: [] };
  },
  /**
   * replaces old uql time for editted uqlItem
   * @param {Object} state
   * @param {Object} payload
   * @param {string} payload.chipKey - _key of uqlItem being editted
   * @param {Object} payload.value - uql object that contains filter row, object type (clause, parens, conjunction),
   *                                 and editted userInput array
   */
  update_filter_row(state, payload) {
    const { keys, path, queryKey } = payload;
    const stateObject = _get(state, `uqlItems[${queryKey}].items[${path}]`, {});

    if (_get(stateObject, 'messages')) _unset(stateObject, 'messages');

    for (const [key, value] of Object.entries(keys)) {
      _set(stateObject, `${key}`, value);

      const splitPath = path.split('.');
      if (splitPath.length === 1) continue; // only paths to _uiRows will have a length longer than 1.
      if (!['field', 'className'].includes(key)) {
        const filterRowIndex = splitPath[0];
        const filterRowObject = _get(state, `uqlItems[${queryKey}].items${filterRowIndex}`);
        _set(filterRowObject, `${key}`, value);
      }
    }
  },
  /**
   * Adds new key to state.uqlItems that is the queryKey
   * @param {Object} state
   * @param {string} queryKey - Key used to store adv search data for each instance of the adv search
   */
  init_adv_search(state, { queryKey = '' }) {
    if (state.uqlItems[queryKey]) return;
    _set(state, `uqlItems.${queryKey}`, { items: [], savedFilters: [] });
  },
  set_saved_filters(state, payload) {
    const { savedFilters, queryKey } = payload;
    const item = _get(state, `uqlItems[${queryKey}]`);
    _set(state, `uqlItems.${queryKey}`, { ...item, savedFilters });
  },
  /**
   * Adds searched query to list of recently searched queries
   * Only allows for 5 recent queries
   * If 5 recent queries exist the last query is removed
   * and the latest is added to the beginning
   * @param {Object} state
   * @param {Object[]} currentUql - Array of current uql items
   */
  set_recent_query(state, { currentUql, queryKey }) {
    let recentQueries = [];
    if (state.recentQueries[queryKey]) {
      recentQueries = [...state.recentQueries[queryKey]];
    }

    if (recentQueries.length < 5) {
      // adds latest query to recent queries
      recentQueries.unshift(currentUql);
    } else {
      // remove last query and adds latest to the beginning
      recentQueries.pop();
      recentQueries.unshift(currentUql);
    }
    _set(state, `recentQueries.${queryKey}`, recentQueries);
  },

  add_ui_row(state, payload) {
    const { path, uiRow, queryKey } = payload;

    state.uqlItems[queryKey].items[path]._uiRows.push(uiRow);
  },

  remove_ui_row_by_index(state, payload) {
    const { uiRowIndex, filterIndex, queryKey } = payload;
    const rows = _get(state, `uqlItems[${queryKey}].items[${filterIndex}]._uiRows`);
    rows.splice(uiRowIndex);
    state.uqlItems[queryKey].items[filterIndex]._uiRows = rows;
  },

  clear_by_query_key(state, queryKey) {
    _unset(state, `uqlItems.${queryKey}`);
  },

  clear_local_state(state) {
    state.uqlItems = {};
    state.recentQueries = {};
  },
};

export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
